/**
 * Cell formatting for report results, driven by the column metadata the backend returns.
 */
import dayjs, { formatDate } from '../../../utils/dayjs';
import { config } from '../../../config/env';

export const EMPTY = '—';

// The app's formatPrice has no thousands separators (e.g. "Rs159780.00"); add them to the
// integer part so large figures are readable, without disturbing the symbol or any conversion.
function groupMoney(str) {
  return String(str).replace(/\d+(?=\.\d)/, (m) => Number(m).toLocaleString());
}

/** Format a datetime in the business timezone, never the viewer's browser zone. */
export function formatBusinessDateTime(value, fmt = 'DD MMM YYYY, hh:mm A') {
  if (!value) return '';
  return dayjs.utc(value).tz(config.businessTimezone).format(fmt);
}

export function alignmentForType(type) {
  return type === 'integer' || type === 'number' || type === 'money' ? 'right' : 'left';
}

const numberFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
const integerFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

/**
 * Returns `(value, column) => string`.
 * `formatPrice` comes from CurrencyContext so money follows the app's currency setting.
 */
export function makeCellFormatter({ formatPrice }) {
  return (value, column) => {
    if (value === null || value === undefined || value === '') return EMPTY;
    const type = column?.type;
    if (type === 'money') return groupMoney(formatPrice ? formatPrice(Number(value)) : numberFormat.format(Number(value)));
    if (type === 'integer') return integerFormat.format(Number(value));
    if (type === 'number') return numberFormat.format(Number(value));
    if (type === 'date' || column?.bucket) return formatBucket(value, column?.bucket);
    if (type === 'datetime') {
      if (column?.tz_shift === false) return String(value).slice(0, 10);
      return formatBusinessDateTime(value);
    }
    if (type === 'boolean') return value ? 'Yes' : 'No';
    if (type === 'enum') {
      const hit = (column?.choices || []).find((c) => c.value === String(value));
      return hit ? hit.label : String(value);
    }
    return String(value);
  };
}

function formatBucket(value, bucket) {
  const s = String(value).slice(0, 10);
  if (bucket === 'month') return s.slice(0, 7);
  if (bucket === 'year') return s.slice(0, 4);
  if (bucket === 'week') return `Week of ${s}`;
  // plain date: avoid timezone shifts by formatting the ISO date directly
  const [y, m, d] = s.split('-');
  if (!y || !m || !d) return formatDate(value);
  return `${d}/${m}/${y}`;
}
